/**
 * Presentation for migration 0025's flow_error* fields.
 *
 * These exist because a call whose nodes all failed still ends through its
 * hangup node and still reports terminal_reason "completed" — so on every
 * reporting surface this project had, a call that played nothing looked
 * exactly like a call that worked. That is not hypothetical: it is how a
 * real deployed regression went unnoticed (docs/PROGRESS.md Slice CD/CE).
 *
 * This module is deliberately a plain .ts rather than living inside the
 * panels' .tsx: this project's vite config only collects `src.../*.test.ts`,
 * and there is no @testing-library/react anywhere in the repo, so logic
 * left in a component cannot be unit tested at all. Keeping the wording and
 * the first-of-N arithmetic here means the part that can actually be got
 * wrong is covered.
 */

/** The subset of a call/adhoc-call row this module reads. */
export interface FlowErrorFields {
  flow_error_node_id?: string;
  flow_error_node_type?: string;
  flow_error?: string;
  flow_error_count?: number;
}

/**
 * True when at least one node took its error path.
 *
 * Keyed on flow_error rather than on the count: the two always agree (the
 * calls_flow_error_complete constraint enforces it server-side), and this
 * way a row from an older API build that predates the count still reads
 * correctly rather than silently reporting "no failure".
 */
export function hasFlowError(row: FlowErrorFields): boolean {
  return Boolean(row.flow_error);
}

/**
 * A one-line human summary, or null when nothing failed.
 *
 * Names the node id and type, not just the cause, because "which node"
 * is what tells someone where to look in their own flow. When several
 * nodes failed it says so explicitly — the stored cause is the FIRST
 * one, and presenting it alone would understate what happened.
 */
export function flowErrorSummary(row: FlowErrorFields): string | null {
  if (!hasFlowError(row)) return null;

  const nodeType = row.flow_error_node_type ?? "unknown";
  const nodeId = row.flow_error_node_id ?? "unknown";
  const count = row.flow_error_count ?? 1;
  const ordinal = count > 1 ? ` (first of ${count} failures)` : "";

  return `The "${nodeId}" ${nodeType} node failed${ordinal}: ${row.flow_error}`;
}

/**
 * The short warning shown next to an otherwise-successful-looking outcome.
 * Null when nothing failed, so a caller can render it unconditionally.
 *
 * Deliberately does NOT claim the call played nothing: a flow can fail one
 * node and still play plenty through others. It reports exactly what is
 * known — part of the flow did not run — and leaves the detail to
 * flowErrorSummary.
 */
export function flowErrorBadge(row: FlowErrorFields): string | null {
  if (!hasFlowError(row)) return null;
  const count = row.flow_error_count ?? 1;
  return count > 1 ? `${count} flow errors` : "flow error";
}
